import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const ExplainerVideo = ({ 
  videoPathEn = '/explainer-en-16x9.mp4',
  videoPathFr = '/explainer-fr-16x9.mp4',
  ctaLink = '#rejoindre'
}) => {
  const { lang } = useLanguage();
  const t = familyTranslations[lang].explainerVideo || {};
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  
  // Select video based on language - 16:9 format videos
  const timestamp = Date.now();
  const videoSrc = lang === 'fr' 
    ? `${videoPathFr}?t=${timestamp}` 
    : `${videoPathEn}?t=${timestamp}`;
  
  // Use the same poster for both languages and all videos
  const posterSrc = '/video-poster-final.jpg';
  
  // Reload video when language changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false);
    }
  }, [lang]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  // Handle video end - reset to show poster again
  const handleVideoEnd = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0; // Reset to beginning to show poster
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      try {
        if (videoRef.current.requestFullscreen) {
          videoRef.current.requestFullscreen().catch(err => {
            console.warn('Fullscreen request failed:', err);
          });
        } else if (videoRef.current.webkitRequestFullscreen) {
          videoRef.current.webkitRequestFullscreen();
        }
      } catch (err) {
        // Fullscreen not allowed in this context (e.g., iframe restrictions)
        console.warn('Fullscreen not available:', err);
      }
    }
  };

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#5B4FE9]/10 text-[#5B4FE9] text-sm font-semibold rounded-full mb-4">
            {t.badge || '📹 Watch Video'}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title || 'See How It Works'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.subtitle || 'Watch this short video to understand how Beyond Memoria helps you care for your loved one\'s grave from anywhere.'}
          </p>
        </div>

        {/* Video Player */}
        <div 
          className="relative rounded-2xl overflow-hidden shadow-2xl bg-black group"
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(isPlaying ? false : true)}
        >
          {/* Video Element - 16:9 format with custom poster */}
          <video
            ref={videoRef}
            className="w-full aspect-video object-cover"
            poster={posterSrc}
            onClick={togglePlay}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleVideoEnd}
            key={`explainer-video-${lang}`}
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Play Button Overlay (shows when paused) */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-opacity duration-300"
              onClick={togglePlay}
            >
              <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-2xl hover:bg-white hover:scale-110 transition-all duration-300">
                <Play className="w-10 h-10 text-[#5B4FE9] ml-1" fill="currentColor" />
              </div>
            </div>
          )}

          {/* Custom Controls */}
          <div 
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 transition-opacity duration-300 ${
              showControls ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="text-white hover:text-[#5B4FE9] transition-colors duration-200"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" fill="currentColor" />
                )}
              </button>

              {/* Mute/Unmute */}
              <button
                onClick={toggleMute}
                className="text-white hover:text-[#5B4FE9] transition-colors duration-200"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? (
                  <VolumeX className="w-6 h-6" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>

              <div className="flex-1" />

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-[#5B4FE9] transition-colors duration-200"
                aria-label="Fullscreen"
              >
                <Maximize className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Video Stats/Trust Signals */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⏱️</span>
            <span>{t.duration || '2 min watch'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">👁️</span>
            <span>{t.views || '10,000+ views'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">⭐</span>
            <span>{t.rating || '4.9/5 rating'}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExplainerVideo;
